import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { RideStatusBadge } from "common-fe";
// #1805 — every timestamp fmt() below (changeset.at, marksConfirmedAt,
// completedAt, statusChangedAt, adminConfirmedAt) is a genuine server-stamped
// audit instant (`new Date().toISOString()`), never an FVD Access stamp, so
// it belongs on the show's real venueTimezone (#1811 split), not the fixed
// FVD stamp zone. #2097: the active show's zone is now wired in through
// useVenueDateTime; a show with none renders "Venue timezone not set" instead
// of the Pacific time the shared resolver's fallback used to print silently.
import useVenueDateTime from "../hooks/useVenueDateTime";
import { useTranslation } from "i18n-fe";
import {
  formatPoints,
  deriveRideTermReason,
  isRideEliminationTerminated,
  TERM_REASONS,
} from "score-common";
import RideTerminatedBanner from "./RideTerminatedBanner";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useLoadedFor } from "../hooks/useLoadedFor";
import { formatPlacing } from "../util/placingDisplay";
import { buildMovementDiffRows, buildSheetLevelDiffRows } from "../util/contenderCompare";
import SheetStateChip, { deriveSheetState } from "./SheetStateChip";
import EntryMethodChip from "./EntryMethodChip";

// #587 — the read+UI half of the two-stage audit (#571). Not secret: any
// admin looking at a Ride can open this from a judge's tab. Presents facts
// only — as-confirmed / as-signed / current, who/when/why per changeset — no
// suspicion scoring, no flagging (#586 was killed deliberately; don't
// reintroduce it here).

// #612 / #1805 / #2097 - audit-trail instants render venue-local (the viewer's
// browser timezone must never leak into an audit trail), with the resolved
// zone label beside the time. Distinct from util/classDisplay.js's
// FVD_STAMP_TIMEZONE: these are genuine server instants, not FVD stamps.
const AUDIT_DATETIME = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
};

function useFmt() {
  const format = useVenueDateTime(AUDIT_DATETIME);
  return (at) => (at ? format(at) : "—");
}

// Union of movement numbers across whichever of the three snapshots exist,
// so a movement only present in one still gets a row instead of silently
// dropping.
function movementReconciliation(confirmedSnap, signedSnap, currentSnap) {
  const numbers = new Set();
  for (const snap of [confirmedSnap, signedSnap, currentSnap]) {
    for (const m of snap?.movementScores ?? []) numbers.add(m.movementNumber);
  }
  return [...numbers]
    .sort((a, b) => a - b)
    .map((num) => {
      const find = (snap) => snap?.movementScores?.find((m) => m.movementNumber === num) ?? null;
      const confirmed = find(confirmedSnap);
      const signed = find(signedSnap);
      const current = find(currentSnap);
      const changedAfterConfirm =
        confirmed && current && (confirmed.score !== current.score || confirmed.flagged !== current.flagged);
      return { num, confirmed, signed, current, changedAfterConfirm };
    });
}

function MarkCell({ mark }) {
  const { t } = useTranslation("scoreadmin");
  if (!mark) return <TableCell>—</TableCell>;
  return (
    <TableCell>
      {mark.score ?? "—"}
      {mark.flagged ? (
        <Chip size="small" label={t("sheetAudit.flaggedLabel")} sx={{ ml: 0.5 }} />
      ) : null}
    </TableCell>
  );
}

function ChangesetRow({ changeset }) {
  const fmt = useFmt();
  const exceptional = changeset.classification === "EXCEPTIONAL";
  return (
    <TableRow>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmt(changeset.at)}</TableCell>
      <TableCell>{changeset.actorSub || "—"}</TableCell>
      <TableCell>
        <Chip
          size="small"
          color={exceptional ? "warning" : "default"}
          variant={exceptional ? "filled" : "outlined"}
          label={changeset.classification}
        />
      </TableCell>
      <TableCell>{changeset.reason || "—"}</TableCell>
      <TableCell>
        {(changeset.fields ?? []).map((f, i) => (
          <Box key={i} sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" component="div">
              {f.field}
            </Typography>
            <Typography variant="body2" component="div">
              {JSON.stringify(f.from)} → {JSON.stringify(f.to)}
            </Typography>
          </Box>
        ))}
      </TableCell>
    </TableRow>
  );
}

function SheetSignature({ sheetId, visible }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!visible || !sheetId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = unwrap(await scoreApi.scoreSheets.getScoreSheetSignature(sheetId));
        if (!cancelled) setUrl(res?.url ?? null);
      } catch {
        if (!cancelled) setUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, sheetId, scoreApi]);

  if (!visible) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color="text.primary" gutterBottom>
        {t("sheetAudit.signatureTitle")}
      </Typography>
      {url ? (
        <Box
          component="img"
          src={url}
          alt={t("sheetAudit.signatureAlt")}
          sx={{ maxWidth: 320, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t("sheetAudit.signatureNotAvailable")}
        </Typography>
      )}
    </Box>
  );
}

// #2818 (scribe-resilience G7/"6. Versioning", AC2/AC3) — the version
// history + compare + restore section. `versions` is
// reconstructSheetVersions' replay (one full-content snapshot per
// baseline/changeset, oldest first, per hash) already returned on the audit
// trail's `sheets[x].versions` — no separate fetch. Compare reuses
// #2816's buildMovementDiffRows (a version's snapshot has the same
// `.movementScores` shape a ScoreSheet does, so it drops in unchanged).
// Restore mirrors ContenderCompareDialog's switch-confirm pattern (pick a
// target, confirm in a small dialog, refetch/patch on success) but requires
// a reason, same as commit/unlock already do server-side.
// #3004: buildMovementDiffRows can flag a row as `differs` purely on a
// non-score field (deductions/flagged/remarks/annotated*) that this table's
// per-cell renderer (mark?.score) never shows -- same gap #2986 fixed in
// ContenderCompareDialog. Names ONLY the non-score fields so the hint never
// duplicates what a genuine score difference already shows in the cells.
function nonScoreDiffFields(differingFields) {
  return (differingFields ?? []).filter((f) => f !== "score");
}

// i18n keys must be static literals, not runtime-interpolated (repo rule) --
// mirrors ContenderCompareDialog.jsx's DIFF_FIELD_LABEL_KEYS exactly.
const DIFF_FIELD_LABEL_KEYS = {
  deductions: "sheetAudit.diffField.deductions",
  flagged: "sheetAudit.diffField.flagged",
  remarks: "sheetAudit.diffField.remarks",
  annotatedDirective: "sheetAudit.diffField.annotatedDirective",
  annotatedDescription: "sheetAudit.diffField.annotatedDescription",
};

function SheetVersionHistory({ sheetId, versions, onRestored }) {
  const fmt = useFmt();
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const [compareFromHash, setCompareFromHash] = useState("");
  const [compareToHash, setCompareToHash] = useState("");
  const [restoreTargetHash, setRestoreTargetHash] = useState(null);
  const [restoreReason, setRestoreReason] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState(null);

  // Default the pickers to oldest-vs-current (the comparison an admin
  // almost always wants first) and keep them pointed at a real version if
  // the list changes under them (e.g. a restore just appended a new one).
  useEffect(() => {
    if (!versions?.length) return;
    setCompareFromHash((prev) => (versions.some((v) => v.hash === prev) ? prev : versions[0].hash));
    setCompareToHash((prev) =>
      versions.some((v) => v.hash === prev) ? prev : versions[versions.length - 1].hash
    );
  }, [versions]);

  if (!versions?.length) return null;

  const fromVersion = versions.find((v) => v.hash === compareFromHash) ?? null;
  const toVersion = versions.find((v) => v.hash === compareToHash) ?? null;
  const diffRows =
    fromVersion && toVersion ? buildMovementDiffRows([fromVersion.snapshot, toVersion.snapshot]) : [];
  // #2818 AC2 judge-deny-1 item 3 -- collectives/eliminations/errors/totals
  // and the sheet-level remarks fields, none of which attach to a single
  // movement number so buildMovementDiffRows can't carry them.
  const otherDiffRows =
    fromVersion && toVersion ? buildSheetLevelDiffRows([fromVersion.snapshot, toVersion.snapshot]) : [];

  async function handleConfirmRestore() {
    if (!restoreTargetHash || !restoreReason.trim()) return;
    try {
      setRestoring(true);
      setRestoreError(null);
      const updated = unwrap(
        await scoreApi.scoreSheets.restoreScoreSheetVersion(sheetId, {
          hash: restoreTargetHash,
          reason: restoreReason.trim(),
        })
      );
      onRestored?.(updated);
      setRestoreTargetHash(null);
      setRestoreReason("");
    } catch (err) {
      setRestoreError(err);
    } finally {
      setRestoring(false);
    }
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" color="text.primary" gutterBottom>
        {t("sheetAudit.versionHistoryTitle", { count: versions.length })}
      </Typography>
      <TableContainer sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("sheetAudit.columns.when")}</TableCell>
              <TableCell>{t("sheetAudit.columns.who")}</TableCell>
              <TableCell>{t("sheetAudit.versionTypeColumn")}</TableCell>
              <TableCell>{t("sheetAudit.columns.class")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {versions.map((v, idx) => {
              const isCurrent = idx === versions.length - 1;
              return (
                <TableRow key={v.hash} data-testid="sheet-version-row">
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fmt(v.at)}</TableCell>
                  <TableCell>{v.actorSub || "—"}</TableCell>
                  <TableCell>{t(`sheetAudit.versionType.${v.type}`, v.type)}</TableCell>
                  <TableCell>
                    {v.classification ? (
                      <Chip
                        size="small"
                        color={v.classification === "EXCEPTIONAL" ? "warning" : "default"}
                        variant={v.classification === "EXCEPTIONAL" ? "filled" : "outlined"}
                        label={v.classification}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {isCurrent ? (
                      <Typography variant="caption" color="text.secondary">
                        {t("sheetAudit.versionCurrentLabel")}
                      </Typography>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        data-testid="sheet-version-restore-button"
                        onClick={() => {
                          setRestoreTargetHash(v.hash);
                          setRestoreReason("");
                          setRestoreError(null);
                        }}
                      >
                        {t("sheetAudit.restoreButton")}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {versions.length > 1 && (
        <>
          <Typography variant="subtitle2" color="text.primary" gutterBottom>
            {t("sheetAudit.compareTitle")}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="sheet-version-compare-from-label">{t("sheetAudit.compareFromLabel")}</InputLabel>
              <Select
                labelId="sheet-version-compare-from-label"
                label={t("sheetAudit.compareFromLabel")}
                value={compareFromHash}
                data-testid="sheet-version-compare-from"
                onChange={(e) => setCompareFromHash(e.target.value)}
              >
                {versions.map((v) => (
                  <MenuItem key={v.hash} value={v.hash}>
                    {fmt(v.at)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="sheet-version-compare-to-label">{t("sheetAudit.compareToLabel")}</InputLabel>
              <Select
                labelId="sheet-version-compare-to-label"
                label={t("sheetAudit.compareToLabel")}
                value={compareToHash}
                data-testid="sheet-version-compare-to"
                onChange={(e) => setCompareToHash(e.target.value)}
              >
                {versions.map((v) => (
                  <MenuItem key={v.hash} value={v.hash}>
                    {fmt(v.at)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {diffRows.length > 0 && (
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("sheetAudit.columns.mvmt")}</TableCell>
                    <TableCell>{t("sheetAudit.compareFromLabel")}</TableCell>
                    <TableCell>{t("sheetAudit.compareToLabel")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {diffRows.map((row) => {
                    const nonScoreFields = nonScoreDiffFields(row.differingFields);
                    return (
                      <TableRow
                        key={row.num}
                        data-testid="sheet-version-compare-row"
                        sx={
                          row.differs
                            ? { bgcolor: "warning.main", "& *": { color: "warning.contrastText" } }
                            : undefined
                        }
                      >
                        <TableCell>
                          {row.num}
                          {/* #3004: a row can be highlighted by a field this
                              table never renders per-cell (deductions/flagged/
                              remarks/annotated*) -- e.g. equal scores,
                              different remarks. Name it here so the highlight
                              is never unexplained. */}
                          {nonScoreFields.length > 0 && (
                            <Typography
                              variant="caption"
                              display="block"
                              color="inherit"
                              data-testid="sheet-version-compare-row-diff-hint"
                            >
                              {t("sheetAudit.differsInLabel")}{" "}
                              {nonScoreFields.map((f) => t(DIFF_FIELD_LABEL_KEYS[f])).join(", ")}
                            </Typography>
                          )}
                        </TableCell>
                        {row.marks.map((mark, i) => (
                          <TableCell key={i}>{mark?.score ?? "—"}</TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {otherDiffRows.length > 0 && (
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("sheetAudit.compareOtherFieldColumn")}</TableCell>
                    <TableCell>{t("sheetAudit.compareFromLabel")}</TableCell>
                    <TableCell>{t("sheetAudit.compareToLabel")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {otherDiffRows.map((row) => (
                    <TableRow
                      key={row.field}
                      data-testid="sheet-version-compare-other-row"
                      sx={{ bgcolor: "warning.main", "& *": { color: "warning.contrastText" } }}
                    >
                      <TableCell>{row.field}</TableCell>
                      <TableCell>{JSON.stringify(row.values[0])}</TableCell>
                      <TableCell>{JSON.stringify(row.values[1])}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog
        open={Boolean(restoreTargetHash)}
        onClose={() => (restoring ? undefined : setRestoreTargetHash(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("sheetAudit.restoreConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t("sheetAudit.restoreConfirmBody")}</DialogContentText>
          {restoreError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("sheetAudit.restoreError")}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            required
            label={t("sheetAudit.restoreReasonLabel")}
            value={restoreReason}
            onChange={(e) => setRestoreReason(e.target.value)}
            data-testid="sheet-version-restore-reason"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreTargetHash(null)} disabled={restoring}>
            {t("sheetAudit.cancel")}
          </Button>
          <Button
            onClick={handleConfirmRestore}
            loading={restoring}
            disabled={!restoreReason.trim()}
            variant="contained"
            data-testid="sheet-version-confirm-restore"
          >
            {t("sheetAudit.confirmRestore")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SheetAuditContent({ trail, test }) {
  const fmt = useFmt();
  const { t } = useTranslation("scoreadmin");
  const state = deriveSheetState({
    marksConfirmedAt: trail.marksConfirmedAt,
    completedAt: trail.completedAt,
  });
  const confirmedSnap = trail.baselines?.confirmed?.snapshot ?? null;
  const signedSnap = trail.baselines?.signed?.snapshot ?? null;
  const currentSnap = trail.current ?? null;
  const rows = movementReconciliation(confirmedSnap, signedSnap, currentSnap);
  const hasSigned = Boolean(trail.baselines?.signed);

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <SheetStateChip state={state} />
        {/* #1931 (AC5) — how this sheet's marks got in, on the audit trail's
            own header row. trail.entryMethod comes from getRideAuditTrail
            (score-common's entryMethodOf over the stored provenance), and is
            null for a pre-#1931 sheet, which renders "Entry unknown" — never
            scribe-live (AC4). */}
        <EntryMethodChip method={trail.entryMethod} />
        <Typography variant="body2" color="text.secondary">
          {t("sheetAudit.judgeLabel", { judgeId: trail.judgeId || "—" })}
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("sheetAudit.confirmedLabel")} {fmt(trail.marksConfirmedAt)}
        {trail.marksConfirmedBy
          ? ` ${t("sheetAudit.byActor", { actor: trail.marksConfirmedBy })}`
          : ""}
        {" · "}
        {t("sheetAudit.signedLabel")} {fmt(trail.completedAt)}
        {trail.completedBy ? ` ${t("sheetAudit.byActor", { actor: trail.completedBy })}` : ""}
      </Typography>

      {trail.hasExceptionalChangeAfterConfirm && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("sheetAudit.exceptionalChangeWarning")}
        </Alert>
      )}

      {trail.verification?.valid === false && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("sheetAudit.verificationFailed", { reason: trail.verification.reason })}
        </Alert>
      )}

      {confirmedSnap && rows.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.primary" gutterBottom>
            {t("sheetAudit.marksTableTitle")}
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("sheetAudit.columns.mvmt")}</TableCell>
                  <TableCell>{t("sheetAudit.columns.asConfirmed")}</TableCell>
                  {hasSigned && <TableCell>{t("sheetAudit.columns.asSigned")}</TableCell>}
                  <TableCell>{t("sheetAudit.columns.current")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={r.num}
                    sx={r.changedAfterConfirm ? { bgcolor: "warning.main", "& *": { color: "warning.contrastText" } } : undefined}
                  >
                    <TableCell>{r.num}</TableCell>
                    <MarkCell mark={r.confirmed} />
                    {hasSigned && <MarkCell mark={r.signed} />}
                    <MarkCell mark={r.current} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {/* #1081 — as-confirmed/as-signed/current all go through the same
              test-aware formatPoints as RideEditor's raw-score readout, so
              the possible-max side matches the earned side's decimal count
              (one for a standard test, two for a freestyle) instead of the
              raw unformatted number this used to print. */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("sheetAudit.totalLabel")} {t("sheetAudit.asConfirmedLabel")}{" "}
            {formatPoints(confirmedSnap.totalPoints, test)} / {formatPoints(confirmedSnap.maxPoints, test)}
            {signedSnap
              ? ` · ${t("sheetAudit.asSignedLabel")} ${formatPoints(signedSnap.totalPoints, test)} / ${formatPoints(signedSnap.maxPoints, test)}`
              : ""}
            {currentSnap
              ? ` · ${t("sheetAudit.currentLabel")} ${formatPoints(currentSnap.totalPoints, test)} / ${formatPoints(currentSnap.maxPoints, test)}`
              : ""}
          </Typography>
        </>
      )}

      <SheetSignature sheetId={trail.sheetId} visible={hasSigned} />

      <Typography variant="subtitle2" color="text.primary" gutterBottom>
        {t("sheetAudit.changesetsTitle", { count: trail.changesets?.length ?? 0 })}
      </Typography>
      {trail.changesets?.length ? (
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("sheetAudit.columns.when")}</TableCell>
                <TableCell>{t("sheetAudit.columns.who")}</TableCell>
                <TableCell>{t("sheetAudit.columns.class")}</TableCell>
                <TableCell>{t("sheetAudit.columns.why")}</TableCell>
                <TableCell>{t("sheetAudit.columns.whatChanged")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trail.changesets.map((c) => (
                <ChangesetRow key={c.hash} changeset={c} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("sheetAudit.noAmendments")}
        </Typography>
      )}

      <Typography variant="subtitle2" color="text.primary" gutterBottom>
        {t("sheetAudit.timelineTitle")}
      </Typography>
      <Stack spacing={0.5}>
        {(trail.events ?? []).map((ev, i) => (
          <Typography key={i} variant="body2" color="text.secondary">
            {fmt(ev.at)} — {ev.type}
            {ev.actorSub ? ` (${ev.actorSub})` : ""}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

// `open` mounts the fetch; `rideId` + `sheetId` scope the view to the judge
// tab the admin was already looking at in RideEditor (a ride can carry one
// ScoreSheet per judge — this dialog shows the one in focus, not all of them,
// so it stays legible).
//
// #3135 -- `ride` + `sheets` (RideEditor's own state, which carries the ride's
// terminationReason/noShow and each sheet's `authoritative` flag) feed the
// ride-level term-reason banner. The audit-trail response carries neither
// (trail.ride has no terminationReason), so without them the dialog falls back
// to the trail's own per-sheet `current.eliminations`.
export default function SheetAuditDialog({ open, onClose, rideId, sheetId, test, onRestored, ride, sheets }) {
  const fmt = useFmt();
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const [error, setError] = useState(null);
  const [trail, setTrail] = useState(null);
  // Bumped after a successful restore (AC3) so the trail refetches: a
  // restore appends a new version/changeset via the normal stream-driven
  // audit capture, and this dialog's own fetch is the simplest way to see
  // it reflected without duplicating getRideAuditTrail's shape here.
  const [refreshToken, setRefreshToken] = useState(0);
  // #3186 -- derived, so the spinner is already up on the first draw after the
  // dialog opens (a flag set inside the effect let "no trail" flash first).
  const loadKey = open && rideId ? `${rideId}:${refreshToken}` : null;
  const [loading, markLoaded] = useLoadedFor(loadKey);

  useEffect(() => {
    markLoaded(null);
    if (!open || !rideId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        const data = unwrap(await scoreApi.scoreSheets.getRideAuditTrail(rideId));
        if (!cancelled) setTrail(data);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) markLoaded(`${rideId}:${refreshToken}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, rideId, scoreApi, refreshToken, markLoaded]);

  function handleRestored(updatedSheet) {
    onRestored?.(updatedSheet);
    setRefreshToken((n) => n + 1);
  }

  const sheetTrail = trail?.sheets?.find((s) => s.sheetId === sheetId) ?? null;

  // #3135 -- the RIDE's term reason, shown on every sheet's trail (a sibling
  // judge's elimination lives on that judge's sheet, never on this one).
  const termRide = ride ?? trail?.ride ?? null;
  const termSheets =
    sheets ?? (trail?.sheets ?? []).map((s) => ({ id: s.sheetId, eliminations: s.current?.eliminations ?? [] }));
  const rideTermReason = isRideEliminationTerminated(termRide, termSheets)
    ? (deriveRideTermReason(termRide, termSheets) ?? TERM_REASONS.ELIMINATED)
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t("sheetAudit.title")}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error.message}</Alert>}
        {!loading && !error && !sheetTrail && (
          <Typography color="text.secondary">{t("sheetAudit.noTrail")}</Typography>
        )}
        {!loading && !error && sheetTrail && (
          <>
            <RideTerminatedBanner reason={rideTermReason} data-testid="sheet-audit-ride-terminated-banner" />
            <SheetAuditContent trail={sheetTrail} test={test} />
            <SheetVersionHistory
              sheetId={sheetTrail.sheetId}
              versions={sheetTrail.versions}
              onRestored={handleRestored}
            />
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">
                {t("sheetAudit.rideStatusLabel")}
              </Typography>
              <RideStatusBadge status={trail.ride?.status} data-testid="sheet-audit-ride-status-badge" />
              <Typography variant="caption" color="text.secondary" data-testid="sheet-audit-placing-line">
                {trail.ride?.statusChangedAt
                  ? t("sheetAudit.changedAt", { date: fmt(trail.ride.statusChangedAt) })
                  : ""}
                {" · "}
                {t("sheetAudit.placingLabel", { placing: formatPlacing(t, trail.ride?.placing) })}
                {trail.ride?.adminConfirmedAt
                  ? ` · ${t("sheetAudit.adminFinalized", { date: fmt(trail.ride.adminConfirmedAt) })}`
                  : ""}
              </Typography>
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
